import { useCallback, useRef } from 'react';
import { message } from 'antd';
import { useTransactionMenuStore } from '../stores/transaction-menu-store';
import { 
    TransactionMenuConfigDTO, 
    TransactionMenuFormValues,
    TransactionType 
} from '../types';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';

const API_BASE = '/api/transaction-menus';

export const useTransactionMenus = () => {
    // Extract individual actions from store to avoid dependency issues
    const {
        menus,
        selectedMenu,
        isLoading,
        isSubmitting,
        error,
        transactionTypes,
        filterType,
        showInactive,
        setMenus,
        setSelectedMenu,
        setLoading,
        setSubmitting,
        setError,
        setTransactionTypes,
        setFilterType,
        setShowInactive
    } = useTransactionMenuStore();

    // Use ref to track showInactive for callbacks without causing re-renders
    const showInactiveRef = useRef(showInactive);
    showInactiveRef.current = showInactive;

    // Fetch all menus (admin view)
    const fetchAllMenus = useCallback(async (includeInactive = false) => {
        setLoading(true);
        setError(null);
        try {
            const response = await AXIOS_INSTANCE.get<TransactionMenuConfigDTO[]>(
                `${API_BASE}/admin`,
                { params: { includeInactive } }
            );
            setMenus(response.data);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to fetch transaction menus';
            setError(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setError, setMenus]);

    // Fetch accessible menus for current user
    const fetchAccessibleMenus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await AXIOS_INSTANCE.get<TransactionMenuConfigDTO[]>(API_BASE);
            setMenus(response.data);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to fetch accessible menus';
            setError(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setError, setMenus]);

    // Fetch menus by transaction type
    const fetchMenusByType = useCallback(async (transactionType: TransactionType) => {
        setLoading(true);
        setError(null);
        try {
            const response = await AXIOS_INSTANCE.get<TransactionMenuConfigDTO[]>(
                `${API_BASE}/type/${transactionType}`
            );
            setMenus(response.data);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to fetch menus by type';
            setError(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [setLoading, setError, setMenus]);

    // Fetch transaction types
    const fetchTransactionTypes = useCallback(async () => {
        try {
            const response = await AXIOS_INSTANCE.get(`${API_BASE}/types`);
            setTransactionTypes(response.data);
        } catch (err: any) {
            console.error('Failed to fetch transaction types:', err);
        }
    }, [setTransactionTypes]);

    // Get menu by ID
    const getMenuById = useCallback(async (id: number): Promise<TransactionMenuConfigDTO | null> => {
        try {
            const response = await AXIOS_INSTANCE.get<TransactionMenuConfigDTO>(`${API_BASE}/${id}`);
            return response.data;
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Failed to fetch menu';
            message.error(errorMsg);
            return null;
        }
    }, []);

    // Create menu
    const createMenu = useCallback(async (data: TransactionMenuFormValues): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.post<TransactionMenuConfigDTO>(API_BASE, data);
            message.success('Transaction menu created successfully');
            await fetchAllMenus(showInactiveRef.current);
            return true;
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to create transaction menu';
            setError(errorMsg);
            message.error(errorMsg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError, fetchAllMenus]);

    // Update menu
    const updateMenu = useCallback(async (id: number, data: TransactionMenuFormValues): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.put<TransactionMenuConfigDTO>(`${API_BASE}/${id}`, data);
            message.success('Transaction menu updated successfully');
            await fetchAllMenus(showInactiveRef.current);
            return true;
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to update transaction menu';
            setError(errorMsg);
            message.error(errorMsg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError, fetchAllMenus]);

    // Delete menu
    const deleteMenu = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        try {
            await AXIOS_INSTANCE.delete(`${API_BASE}/${id}`);
            message.success('Transaction menu deleted successfully');
            await fetchAllMenus(showInactiveRef.current);
            return true;
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to delete transaction menu';
            setError(errorMsg);
            message.error(errorMsg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, setError, fetchAllMenus]);

    // Activate menu
    const activateMenu = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        try {
            await AXIOS_INSTANCE.patch(`${API_BASE}/${id}/activate`);
            message.success('Transaction menu activated');
            await fetchAllMenus(showInactiveRef.current);
            return true;
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to activate menu';
            message.error(errorMsg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, fetchAllMenus]);

    // Deactivate menu
    const deactivateMenu = useCallback(async (id: number): Promise<boolean> => {
        setSubmitting(true);
        try {
            await AXIOS_INSTANCE.patch(`${API_BASE}/${id}/deactivate`);
            message.success('Transaction menu deactivated');
            await fetchAllMenus(showInactiveRef.current);
            return true;
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Failed to deactivate menu';
            message.error(errorMsg);
            return false;
        } finally {
            setSubmitting(false);
        }
    }, [setSubmitting, fetchAllMenus]);

    return {
        // State
        menus,
        selectedMenu,
        isLoading,
        isSubmitting,
        error,
        transactionTypes,
        filterType,
        showInactive,

        // Actions
        setSelectedMenu,
        setFilterType,
        setShowInactive,

        // API Operations
        fetchAllMenus,
        fetchAccessibleMenus,
        fetchMenusByType,
        fetchTransactionTypes,
        getMenuById,
        createMenu,
        updateMenu,
        deleteMenu,
        activateMenu,
        deactivateMenu
    };
};
